// src/pages/about/Newsroom.js
import { Link } from "react-router-dom";
import React from "react";
import { Helmet } from "react-helmet";
import styled from "styled-components";
import LazyLoad from "react-lazyload";
import {
  FaNewspaper,
  FaFileDownload,
  FaBlog,
  FaBullhorn,
  FaTv,
  FaAddressBook,
  FaFacebook,
  FaInstagram,
  FaLinkedin,
  FaTwitter,
  FaYoutube,
} from "react-icons/fa";
import SocialMediaIcons from "../../components/SocialMediaIcons";

const Newsroom = () => {
  return (
    <>
      <Helmet>
        <title>Newsroom - Habitat for Humanity Buffalo</title>
        <meta
          name="description"
          content="Stay updated with the latest news, multimedia, and resources from Habitat for Humanity Buffalo. Explore our newsletter, blog, media coverage, and more."
        />
      </Helmet>
      <Container className="container">
        <h1>Newsroom</h1>
        <h2 className="section-header primary">
          We're Always Building at Habitat Buffalo
        </h2>
        <Description>
          From building homes to communities and hope, our newsroom has the
          latest information covering Habitat Buffalo news, multimedia, and
          resources. Connect with us today to learn more about how together, we
          can build a world where everyone has a decent place to live.
        </Description>

        <Section>
          <GridContainer>
            <GridItem>
              <LazyLoad height={50} offset={100}>
                <FaNewspaper size={50} className="icon-image" />
              </LazyLoad>
              <Title>Habitat Happenings Newsletter</Title>
              <Text>See what's "happening" at Habitat Buffalo!</Text>
              <Button
                to="/newsroom/habitat-happenings"
                className="button primary"
              >
                READ MORE
              </Button>
            </GridItem>
            <GridItem>
              <LazyLoad height={50} offset={100}>
                <FaFileDownload size={50} className="icon-image" />
              </LazyLoad>
              <Title>All the Good Stuff</Title>
              <Text>
                Check out our downloadable PDFs, images, photos, and videos!
              </Text>
              <Button
                to="/newsroom/all-the-good-stuff"
                className="button primary"
              >
                DOWNLOAD
              </Button>
            </GridItem>
            <GridItem>
              <LazyLoad height={50} offset={100}>
                <FaBlog size={50} className="icon-image" />
              </LazyLoad>
              <Title>Blogs</Title>
              <Text>
                Our blog is a spot where we can share even more news and
                stories.
              </Text>
              <Button to="/newsroom/blog" className="button primary">
                READ MORE
              </Button>
            </GridItem>
            <GridItem>
              <LazyLoad height={50} offset={100}>
                <FaBullhorn size={50} className="icon-image" />
              </LazyLoad>
              <Title>Hot Off the Press</Title>
              <Text>
                Stay in the know with the latest news on Habitat Buffalo.
              </Text>
              <Button to="/newsroom/latest-news" className="button primary">
                DOWNLOAD
              </Button>
            </GridItem>
            <GridItem>
              <LazyLoad height={50} offset={100}>
                <FaTv size={50} className="icon-image" />
              </LazyLoad>
              <Title>In The Media</Title>
              <Text>
                Here are the radio, social channels, and TV coverage from
                Habitat Buffalo!
              </Text>
              <Button to="/newsroom/in-the-media" className="button primary">
                CONNECT
              </Button>
            </GridItem>
            <GridItem>
              <LazyLoad height={50} offset={100}>
                <FaFileDownload size={50} className="icon-image" />
              </LazyLoad>
              <Title>Financials</Title>
              <Text>
                Annual Reports, 990s, and other organizational documents.
              </Text>
              <Button to="/newsroom/financials" className="button primary">
                VIEW REPORTS
              </Button>
            </GridItem>
          </GridContainer>
        </Section>
        <Section>
          <SectionTitle>Media Contact</SectionTitle>
          <Description>
            For media-related inquiries, please contact us using the information
            below or fill out the form and we'll get back to you as soon as
            possible.
          </Description>
          <ContactCard>
            <ContactInfo>
              <p>
                <strong>Jim Montour, Director of Communications</strong>
              </p>
              <p>
                <strong>Email:</strong>{" "}
                <a href="mailto:jmontour@habitatbuffalo.org">
                  jmontour@habitatbuffalo.org
                </a>
              </p>
              <p>
                <strong>Phone:</strong>{" "}
                <a href="tel:+17162595918">(716) 259-5918</a>
              </p>
              <p>
                <strong>Address:</strong> Habitat for Humanity Buffalo, 1675
                South Park Ave, Buffalo, NY 14220
              </p>
            </ContactInfo>
          </ContactCard>
        </Section>

        <Section>
          <SectionTitle>Connect on Social Media</SectionTitle>
          <SocialMediaIcons />
        </Section>
      </Container>
    </>
  );
};

const Container = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: var(--container-padding);
`;

const Description = styled.p`
  font-size: 18px;
  text-align: center;
  margin-bottom: 30px;
`;

const Section = styled.div`
  margin-bottom: 40px;
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;

  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr; /* Single column on mobile */
  }
`;

const GridItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 20px;
  border: 1px solid #ddd;
  border-radius: 10px;
  background-color: #fff;
  min-height: 300px; /* Ensures consistency in height */
`;

const Title = styled.h3`
  font-size: 20px;
  color: var(--primary-color);
  margin-bottom: 10px;
`;

const Text = styled.p`
  font-size: 16px;
  margin-bottom: auto; /* Pushes the button to the bottom */
`;

const Button = styled(Link)`
  background-color: var(--primary-color);
  color: var(--secondary-color);
  padding: 10px 20px;
  text-decoration: none;
  border-radius: 5px;
  font-size: 14px;
  font-weight: bold;
  margin-top: 20px; /* Adds some space between text and button */

  &:hover {
    background-color: var(--accent-color);
  }
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  text-align: center;
  margin-bottom: 20px;
`;

const SocialIconContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
`;

const SocialIcon = styled.a`
  color: inherit;
  &:hover {
    color: var(--primary-color);
  }
`;

const ContactCard = styled.div`
  background-color: #fff;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  padding: 20px;
  margin-top: 20px;
`;

const ContactInfo = styled.div`
  font-size: 18px;
  text-align: center;
  p {
    margin: 10px 0;
    a {
      color: var(--primary-color);
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }
  }
`;

export default Newsroom;
